/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { OcrService } from '../ocr/ocr.service';

export interface TrashRow {
  id: string;
  name: string;
  isFolder: boolean;
  /** Folder the node was deleted from. */
  originalLocation: string;
  deletedAt: string;
  deletedByName: string;
  sizeInBytes?: number;
  documentType: string;
  /** Whole days the node has been in the trash. */
  daysInTrash: number;
}

export interface TrashPage {
  rows: TrashRow[];
  total: number;
}

/** Outcome of a bulk restore or purge, per item. */
export interface BulkResult {
  ok: string[];
  failed: { id: string; name: string; reason: string }[];
}

/**
 * The trash (UI-10), backed by Alfresco's Trashcan API.
 *
 * Deliberately has no notion of a retention countdown. This repository exposes
 * no trashcan retention policy — `/api/admin/trashcan` and the enterprise
 * trashcan console both return 404 — so nothing on the server will remove these
 * nodes on a schedule. A "time remaining" column would therefore be a number the
 * UI made up; the screen reports how long an item has *been* in the trash, which
 * is a fact, and leaves permanent deletion to the explicit action.
 */
@Injectable({ providedIn: 'root' })
export class TrashService {
  private readonly api = inject(AlfrescoApiService);
  private readonly ocr = inject(OcrService);

  private call(path: string, method: string, body?: any, query?: any): Observable<any> {
    const inst: any = this.api.getInstance();
    return from(
      inst.contentClient.callApi(path, method, {}, query ?? {}, {}, {}, body ?? null, ['application/json'], ['application/json']) as Promise<any>
    );
  }

  /** One page of deleted nodes, newest deletion first. */
  page(skipCount: number, maxItems: number): Observable<TrashPage> {
    return this.call('/deleted-nodes', 'GET', null, { skipCount, maxItems, include: 'path,properties' }).pipe(
      map((res) => ({
        rows: (res?.list?.entries ?? []).map((e: any) => this.toRow(e.entry)),
        total: res?.list?.pagination?.totalItems ?? 0
      }))
    );
  }

  /**
   * Every deleted node, walked across all pages — for "delete all". Only the
   * row fields a purge needs (id, and name for error reporting) are read, so
   * no `include` is requested.
   */
  allRows(): Observable<TrashRow[]> {
    const collect = (skipCount: number, acc: TrashRow[]): Observable<TrashRow[]> =>
      this.call('/deleted-nodes', 'GET', null, { skipCount, maxItems: 200 }).pipe(
        switchMap((res) => {
          const batch: TrashRow[] = (res?.list?.entries ?? []).map((e: any) => this.toRow(e.entry));
          const all = acc.concat(batch);
          return res?.list?.pagination?.hasMoreItems && batch.length ? collect(skipCount + batch.length, all) : of(all);
        })
      );
    return collect(0, []);
  }

  /**
   * Restores one node to the folder it was deleted from.
   *
   * Sent with **no** request body: this repository rejects even an empty JSON
   * object here with `400 Node id may not be null`.
   */
  restore(id: string): Observable<any> {
    return this.call(`/deleted-nodes/${id}/restore`, 'POST');
  }

  /**
   * Permanently removes one node. This cannot be undone.
   *
   * The words read off the document are held outside Alfresco, in the OCR index,
   * so they do not go with it — the repository knows nothing about them. This is
   * the one moment they should be forgotten: everywhere else the node can still
   * come back. Nothing is asked of the index until Alfresco has actually
   * accepted the deletion, so a refused purge never loses the text.
   *
   * If the index cannot be reached the purge is still reported as done, because
   * it is: the document is gone. What would be left behind is text for an id
   * that no longer resolves, which search discards anyway — every hit is put to
   * Alfresco before it is shown, and Alfresco has no answer for a node that is
   * not there. Failing the whole operation over that would say something untrue.
   */
  purge(id: string): Observable<any> {
    return this.call(`/deleted-nodes/${id}`, 'DELETE').pipe(
      switchMap((result) =>
        this.ocr.removeFromIndex(id).pipe(
          catchError(() => of(false)),
          map(() => result)
        )
      )
    );
  }

  private toRow(entry: any): TrashRow {
    const archivedAt: string = entry.archivedAt ?? '';
    return {
      id: entry.id,
      name: entry.name,
      isFolder: !!entry.isFolder,
      originalLocation: this.locationOf(entry),
      deletedAt: archivedAt,
      deletedByName: entry.archivedByUser?.displayName ?? entry.archivedByUser?.id ?? '',
      sizeInBytes: entry.content?.sizeInBytes,
      documentType: entry.properties?.['dms:documentType'] ?? '',
      daysInTrash: this.daysSince(archivedAt)
    };
  }

  private locationOf(entry: any): string {
    const path: string = entry?.path?.name ?? '';
    if (!path) {
      return '';
    }
    return path.replace(/^\/Company Home\/?/, '').replace(/\//g, ' / ') || 'Company Home';
  }

  private daysSince(iso: string): number {
    if (!iso) {
      return 0;
    }
    const elapsed = Date.now() - new Date(iso).getTime();
    return Math.max(0, Math.floor(elapsed / (24 * 60 * 60 * 1000)));
  }
}
